import {
  Box,
  Button,
  Flex,
  FormControl,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaPlus, FaSearch } from "react-icons/fa";
import { RemovableFilterChip } from "../components/RemovableFilterChip";
import {
  type ColumnFilterPart,
  activeFilterParts,
  clearFilterPart,
  filtersAsText,
  filterValueText,
  isFilterable,
  SEARCH_MAX_LENGTH,
} from "./columnFilters";
import { ColumnSearchEditor, ColumnValueEditor } from "./ColumnEditors";
import type { EntityTableColumn } from "./EntityTable";
import { Tooltip } from "../components/Tooltip";
import { TextLengthHint } from "../input/TextLengthHint";

export type ExtraFilterChip = { label: string; onRemove: () => void };

export type TableSearch = { value: string; onChange: (value: string) => void; label: string; tooltip: string };

export const FilterBar = ({
  columns,
  extraChips = [],
  defaultFilterColumnKeys = [],
  onResetAll,
  countText,
  debounceMs = 300,
  search,
  onDropdownOpen,
  onDropdownClose,
  offersHiddenColumns = false,
}: {
  columns: EntityTableColumn[];
  extraChips?: ExtraFilterChip[];
  defaultFilterColumnKeys?: string[];
  onResetAll: () => void;
  countText?: string | null;
  debounceMs?: number;
  search?: TableSearch;
  onDropdownOpen?: (dropdownKey: string) => void;
  onDropdownClose?: (dropdownKey: string) => void;
  offersHiddenColumns?: boolean;
}) => {
  const { t } = useTranslation();
  const dropdown = useMemo(() => ({ onOpen: onDropdownOpen, onClose: onDropdownClose }), [onDropdownOpen, onDropdownClose]);
  const chips = columns
    .filter((column) => !defaultFilterColumnKeys.includes(column.key))
    .flatMap((column) => activeFilterParts(column).map((part) => ({ column, part, value: filterValueText(t, column, part) })));
  const isFiltered = chips.length > 0 || extraChips.length > 0;
  const isSearching = !!search?.value.trim();
  const offered = columns.filter((column) => (offersHiddenColumns || !column.hidden) && isFilterable(column));
  const someFilterHidden = columns.some((column) => column.hidden && isFilterable(column) && !offered.includes(column));

  return (
    <Flex gap="2" rowGap="3" mb="3" wrap="wrap" alignItems="center" data-testid="filter-bar">
      {search && <TableSearchField search={search} />}
      {extraChips.map((chip) => (
        <RemovableFilterChip key={chip.label} label={chip.label} onRemove={chip.onRemove} />
      ))}
      {chips.map(({ column, part, value }) => (
        <ColumnFilterChip
          key={`${column.key}-${part}`}
          column={column}
          part={part}
          value={value}
          debounceMs={debounceMs}
          dropdown={dropdown}
          onRemove={() => clearFilterPart(column, part)}
        />
      ))}
      {offered.length > 0 && (
        <AddFilter offered={offered} someFilterHidden={someFilterHidden} debounceMs={debounceMs} dropdown={dropdown} />
      )}
      {isFiltered && (
        <Button variant="link" colorScheme="teal" size="sm" onClick={onResetAll}>
          {t("Reset filters")}
        </Button>
      )}
      {(isFiltered || isSearching) && countText && (
        <Text fontSize="sm" color="smMuted" ml="auto" data-testid="filter-bar-count">
          {countText}
        </Text>
      )}
    </Flex>
  );
};

const TableSearchField = ({ search }: { search: TableSearch }) => (
  <FormControl width={{ base: "full", md: "64" }}>
    <Tooltip label={search.tooltip}>
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          <Icon as={FaSearch} color="smMuted" />
        </InputLeftElement>
        <Input
          type="search"
          value={search.value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => search.onChange(e.target.value)}
          placeholder={search.label}
          aria-label={search.label}
          maxLength={SEARCH_MAX_LENGTH}
          rounded="md"
        />
      </InputGroup>
    </Tooltip>
    <TextLengthHint text={search.value} max={SEARCH_MAX_LENGTH} />
  </FormControl>
);

const ColumnFilterChip = ({
  column,
  part,
  value,
  debounceMs,
  dropdown,
  onRemove,
}: {
  column: EntityTableColumn;
  part: ColumnFilterPart;
  value: string;
  debounceMs: number;
  dropdown: DropdownReport;
  onRemove: () => void;
}) => {
  const editor = useDisclosure();
  return (
    <Popover isOpen={editor.isOpen} onOpen={editor.onOpen} onClose={editor.onClose} placement="bottom-start" isLazy>
      <PopoverAnchor>
        <RemovableFilterChip label={column.label} value={value} onRemove={onRemove} onOpen={editor.onOpen} />
      </PopoverAnchor>
      <Portal>
        <PopoverContent width="auto" minWidth="64" maxHeight="96" overflowY="auto">
          <PopoverBody p="0">
            {part === "search" ? (
              <ColumnSearchEditor column={column} onClose={editor.onClose} />
            ) : (
              <ReportingValueEditor column={column} debounceMs={debounceMs} onClose={editor.onClose} dropdown={dropdown} />
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const AddFilter = ({
  offered,
  someFilterHidden,
  debounceMs,
  dropdown,
}: {
  offered: EntityTableColumn[];
  someFilterHidden: boolean;
  debounceMs: number;
  dropdown: DropdownReport;
}) => {
  const { t } = useTranslation();
  const picker = useDisclosure();
  const [term, setTerm] = useState("");
  const [chosenKey, setChosenKey] = useState<string | null>(null);
  const filterable = [...offered].sort((a, b) => a.label.localeCompare(b.label, "nb"));
  const matching = filterable.filter((column) => column.label.toLowerCase().includes(term.trim().toLowerCase()));
  const chosen = filterable.find((column) => column.key === chosenKey);

  const close = () => {
    picker.onClose();
    setChosenKey(null);
    setTerm("");
  };

  return (
    <Popover isOpen={picker.isOpen} onOpen={picker.onOpen} onClose={close} placement="bottom-start" isLazy>
      <PopoverTrigger>
        <Button leftIcon={<Icon as={FaPlus} />} variant="outline" colorScheme="gray" color="smMuted" size="sm">
          {t("Add filter")}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="auto" minWidth="64" maxWidth="80">
          {chosen ? (
            <PopoverBody p="0">
              <Box px="3" pt="2">
                <Button
                  leftIcon={<Icon as={FaArrowLeft} />}
                  variant="link"
                  size="sm"
                  color="smPrimary"
                  onClick={() => setChosenKey(null)}
                >
                  {t("Back")}
                </Button>
                <Text fontSize="sm" fontWeight="bold" pt="2">
                  {chosen.label}
                </Text>
              </Box>
              <Box maxHeight="96" overflowY="auto">
                {filtersAsText(chosen) ? (
                  <ColumnSearchEditor column={chosen} onClose={close} />
                ) : (
                  <ReportingValueEditor column={chosen} debounceMs={debounceMs} onClose={close} dropdown={dropdown} />
                )}
              </Box>
            </PopoverBody>
          ) : (
            <>
              <PopoverBody>
                <InputGroup size="sm" mb="2">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={FaSearch} color="smMuted" />
                  </InputLeftElement>
                  <Input
                    value={term}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setTerm(e.target.value)}
                    placeholder={String(t("Search the columns"))}
                    aria-label={String(t("Search the columns"))}
                  />
                </InputGroup>
                <Stack spacing="0" maxHeight="72" overflowY="auto" role="list">
                  {matching.map((column) => (
                    <Box key={column.key} role="listitem">
                      <Button
                        variant="ghost"
                        size="sm"
                        width="full"
                        justifyContent="flex-start"
                        fontWeight="normal"
                        onClick={() => setChosenKey(column.key)}
                      >
                        {column.label}
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </PopoverBody>
              {someFilterHidden && (
                <PopoverFooter>
                  <Text fontSize="sm" color="smMuted">
                    {t("Missing the column? Add it under «Columns».")}
                  </Text>
                </PopoverFooter>
              )}
            </>
          )}
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

interface DropdownReport {
  onOpen?: (dropdownKey: string) => void;
  onClose?: (dropdownKey: string) => void;
}

const ReportingValueEditor = ({
  column,
  debounceMs,
  onClose,
  dropdown,
}: {
  column: EntityTableColumn;
  debounceMs: number;
  onClose: () => void;
  dropdown: DropdownReport;
}) => {
  const { onOpen: reportOpen, onClose: reportClose } = dropdown;
  useEffect(() => {
    const key = `${column.key}-filter`;
    reportOpen?.(key);
    return () => reportClose?.(key);
  }, [column.key, reportOpen, reportClose]);
  return <ColumnValueEditor column={column} debounceMs={debounceMs} onClose={onClose} />;
};
